import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import 'bootstrap/dist/css/bootstrap.min.css';
import BottomNavigation from './components/BottomNavigation';
import Header from './components/Header';
import HomeScreen from './pages/HomeScreen';
import MarketsScreen from './pages/MarketsScreen';
import TradeScreen from './pages/TradeScreen';
import ProfileScreen from './pages/ProfileScreen';
import TransactionHistory from './pages/TransactionHistory';
import FundDeposit from './pages/FundDeposit';
import Test from './pages/Test';
import Blank from './pages/Blank';
import ScratchCardScreen from './pages/ScratchCardScreen';
import StakingScreen from './pages/StakingScreen';
import EditProfileScreen from './pages/EditProfileScreen';
import ViewTeamScreen from './pages/ViewTeamScreen';
import ChangePassword from './pages/ChangePassword';
import CouponScreen from './pages/CouponScreen';
import WithdrawlScreen from './pages/WithdrawlScreen';
import ScrollToTop from './helpers/ScrollToTop';
import SupportScreen from './pages/SupportScreen';
import CommissionScreen from './pages/CommissionScreen';
import ProtectedRoute from './context/ProtectedRoute';
import Error404Screen from './pages/Error404Screen';
import LoginScreen from './pages/LoginScreen';
import { Toaster } from 'react-hot-toast';
import DepositHistory from './pages/DepositHistory';
import Test2 from './pages/Test2';
import WithdrawalHistory from './pages/WithdrawalHistory';
import RegisterScreen from './pages/RegisterScreen';
import CommissionClaim from './pages/CommissionClaim';
import UsdtSwap from './pages/UsdtSwap';
import TradeList from './pages/TradeList';
import NewStakingScreen from './pages/NewStakingScreen';
import MyStakingScreen from './pages/MyStakingScreen';
import StakingCommission from './pages/StakingCommission';
import SwapReportPage from './pages/SwapReportPage';
import UsdtTransactionHistory from './pages/UsdtTransactionHistory';
import SubordinateData from './pages/SubordinateData';
import CommissionRecord from './pages/CommissionRecord';

function AppContent() {
  // Initialize theme from localStorage or default to 'dark'
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'dark';
  });

  const location = useLocation();

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  // Ensure theme is saved & synced to <html> for Bootstrap 5.3
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (!savedTheme) {
      localStorage.setItem('theme', theme);
    }
  }, [theme]);

  useEffect(() => {
    // For global Bootstrap theming
    document.documentElement.setAttribute('data-bs-theme', theme);
  }, [theme]);

  const accessPaths = [
    '/markets',
    '/staking',
    '/new-staking',
    '/my-staking',
    '/staking-commision',
    '/profile',
    '/transaction-history',
    '/usdt-transaction-history',
    '/fund-deposit',
    '/test',
    '/test2',
    '/blank',
    '/scratch-card',
    '/edit-profile',
    '/team',
    '/change-password',
    '/coupon',
    '/withdrawl',
    '/usdt-swap',
    '/swap-report',
    '/support',
    '/commission-chart',
    '/commission-claim',
    '/deposit-history',
    '/withdrawl-history',
    '/subordinate-data',
    '/trade-list',
    '/commission-record',

    '/trade/ticoin',
    '/trade/bitcoin',
    '/trade/ethereum',
    '/trade/binancecoin',
    '/trade/dogecoin',
    '/trade/tron',
    '/trade/solana',
    '/trade/usd-coin',

    '/',
  ];

  const outerBgClass =
    theme === 'light' ? 'bg-light text-dark' : 'bg-dark text-light';
  const innerCardClass =
    theme === 'light' ? 'bg-white text-dark' : 'bg-dark text-light';

  return (
    <div
      className={`min-vh-100 d-flex align-items-center justify-content-center ${outerBgClass}`}
      style={{ overflow: 'hidden' }}
      data-bs-theme={theme}
    >
      <div
        className="w-100 h-100 d-flex align-items-center justify-content-center position-relative"
        style={{ maxWidth: '400px' }}
      >
        {/* Fixed Header */}
        {accessPaths.includes(location.pathname) && (
          <Header theme={theme} toggleTheme={toggleTheme} />
        )}

        {/* Main Content Container */}
        <div
          className={`${innerCardClass} rounded-4 shadow w-100 h-100 d-flex flex-column position-relative overflow-hidden`}
        >
          {/* Scrollable Content Area */}
          <main
            className="flex-grow-1 position-relative overflow-auto"
            style={{
              paddingTop: '70px', // Space for header
              paddingBottom: '70px', // Space for bottom navigation
            }}
          >
            <div className="p-3 h-100">
              <AnimatePresence mode="wait">
                <motion.div
                  key={location.pathname}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="h-100"
                >
                  <Toaster
                    position='top-center'
                    reverseOrder={false}
                    toastOptions={{
                      duration: 3000,
                      position: 'top-center'
                    }}
                  />

                  <Routes location={location}>
                    {/* HOME */}
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <HomeScreen theme={theme} />
                        </ProtectedRoute>
                      }
                    />

                    {/* PROFILE PAGES */}
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <ProfileScreen theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/change-password"
                      element={
                        <ProtectedRoute>
                          <ChangePassword theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/transaction-history"
                      element={
                        <ProtectedRoute>
                          <TransactionHistory theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/usdt-transaction-history"
                      element={
                        <ProtectedRoute>
                          <UsdtTransactionHistory theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/fund-deposit"
                      element={
                        <ProtectedRoute>
                          <FundDeposit theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/edit-profile"
                      element={
                        <ProtectedRoute>
                          <EditProfileScreen theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/withdrawl"
                      element={
                        <ProtectedRoute>
                          <WithdrawlScreen theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/usdt-swap"
                      element={
                        <ProtectedRoute>
                          <UsdtSwap theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/swap-report"
                      element={
                        <ProtectedRoute>
                          <SwapReportPage theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/subordinate-data"
                      element={
                        <ProtectedRoute>
                          <SubordinateData theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/commission-chart"
                      element={
                        <ProtectedRoute>
                          <CommissionScreen theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/commission-claim"
                      element={
                        <ProtectedRoute>
                          <CommissionClaim theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/deposit-history"
                      element={
                        <ProtectedRoute>
                          <DepositHistory theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/withdrawl-history"
                      element={
                        <ProtectedRoute>
                          <WithdrawalHistory theme={theme} />
                        </ProtectedRoute>
                      }
                    />

                    {/* TRADE PAGES */}
                    <Route
                      path="/coupon"
                      element={
                        <ProtectedRoute>
                          <CouponScreen theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/team"
                      element={
                        <ProtectedRoute>
                          <ViewTeamScreen theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/scratch-card"
                      element={
                        <ProtectedRoute>
                          <ScratchCardScreen theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/markets"
                      element={
                        <ProtectedRoute>
                          <MarketsScreen theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/trade/:entity_id"
                      element={
                        <ProtectedRoute>
                          <TradeScreen theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/trade-list"
                      element={
                        <ProtectedRoute>
                          <TradeList theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/commission-record"
                      element={
                        <ProtectedRoute>
                          <CommissionRecord theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/staking"
                      element={
                        <ProtectedRoute>
                          <StakingScreen theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/new-staking"
                      element={
                        <ProtectedRoute>
                          <NewStakingScreen theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/my-staking"
                      element={
                        <ProtectedRoute>
                          <MyStakingScreen theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/staking-commision"
                      element={
                        <ProtectedRoute>
                          <StakingCommission theme={theme} />
                        </ProtectedRoute>
                      }
                    />

                    {/* COMPANY PAGES */}
                    <Route
                      path="/support"
                      element={
                        <ProtectedRoute>
                          <SupportScreen theme={theme} />
                        </ProtectedRoute>
                      }
                    />

                    {/* LOGIN RELATED */}
                    <Route path="/login" element={<LoginScreen theme={theme} />} />
                    <Route
                      path="/register/:refer_code?"
                      element={<RegisterScreen theme={theme} />}
                    />

                    {/* OTHERS */}
                    <Route
                      path="*"
                      element={
                        <ProtectedRoute>
                          <Error404Screen theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/test"
                      element={
                        <ProtectedRoute>
                          <Test theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/test2"
                      element={
                        <ProtectedRoute>
                          <Test2 theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/blank"
                      element={
                        <ProtectedRoute>
                          <Blank theme={theme} />
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </motion.div>
              </AnimatePresence>
            </div>
          </main>
        </div>

        {/* Fixed Bottom Navigation */}
        {accessPaths.includes(location.pathname) && (
          <BottomNavigation theme={theme} />
        )}
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <AppContent />
    </Router>
  );
}

export default App;
